import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import usePurchase from 'hooks/store/usePurchase';
import { googlePayPaymentAttempt } from '../../actions/googlePay';
import { updatePurchase } from '../../actions/purchase';
import { setError } from '../../actions';
import errorMonitoring from '../../errorMonitoring';
import useAdyenGooglePaySdk from './useAdyenGooglePaySdk';

/**
 * useAdyenGooglePay hook.
 * Orchestrate Redux state, i18n, error monitoring, and payment handlers for Google Pay.
 * Compose useAdyenGooglePaySdk for all Adyen SDK lifecycle management.
 *
 * Unlike Apple Pay, Google Pay has a linear flow:
 * - The payment sheet closes automatically before onSubmit fires
 * - onSubmit is fire-and-forget (not awaited by the component)
 * - No async coordination between onSubmit and onAuthorized is needed
 *
 * @param {object} props - onPaymentButtonClick.
 * @param {Function} props.onPaymentButtonClick - Callback when Google Pay button is clicked.
 * @param {Function} [props.beforeWalletPayment] - Optional current Quick Purchase check before dispatch.
 * @returns {object} Google Pay component state and references.
 */
function useAdyenGooglePay({ onPaymentButtonClick, beforeWalletPayment }) {
  const dispatch = useDispatch();
  const beforeWalletPaymentRef = useRef(beforeWalletPayment);
  beforeWalletPaymentRef.current = beforeWalletPayment;
  const { t } = useTranslation('payment');
  const { env, copies } = useSelector((state) => state.whitelabelConfig);
  const { currency } = env;
  const WhitelabelName = copies?.provider?.name ?? '';
  const { adyen: adyenConfig } = env;

  const { total: purchaseTotal, token: purchaseToken } = usePurchase();

  /**
   * Handles internal Google Pay errors (from try/catch blocks).
   * @param {Object} err - Error object.
   * @param {string} customMessage - Custom error message to show to user.
   * @param {string} sentryInfo - Custom info message for Sentry.
   */
  const handleError = useCallback(
    (err, customMessage, sentryInfo = 'Error on Google Pay (Adyen)') => {
      errorMonitoring.notify({
        error: err,
        info: sentryInfo,
      });

      dispatch(updatePurchase(false));

      dispatch(
        setError(
          301,
          'error_generating_payment',
          'warning',
          false,
          customMessage || t('google_pay_error.generic_error'),
        ),
      );
    },
    [dispatch, t],
  );

  /**
   * Handles errors from Adyen Google Pay component.
   * Adyen v4.x passes only the error object as parameter.
   * Google Pay uses statusCode: 'CANCELED' for user cancellation.
   * @param {Object} adyenError - Error object from Adyen component
   */
  const handleComponentError = useCallback(
    (adyenError) => {
      const errorMessage = adyenError?.error?.message || adyenError?.message || 'Unknown error';
      const errorStatusCode = adyenError?.statusCode || adyenError?.error?.statusCode;

      console.error(`Google Pay Adyen Error: ${errorMessage}`);

      if (errorStatusCode === 'CANCELED' || errorMessage.toLowerCase().includes('cancel')) {
        console.log('Google Pay: User cancelled payment');
        dispatch(updatePurchase(false));
        return;
      }

      errorMonitoring.notify({
        error: adyenError,
        info: `Google Pay Error: ${errorMessage}`,
      });

      dispatch(updatePurchase(false));

      let userMessage;
      if (errorMessage.toLowerCase().includes('network')) {
        userMessage = t('google_pay_error.network_error');
      } else {
        userMessage = t('google_pay_error.generic_error');
      }

      dispatch(setError(301, 'error_generating_payment', 'warning', false, userMessage));
    },
    [dispatch, t],
  );

  /**
   * Handles Google Pay button click event.
   * Triggers validation via onPaymentButtonClick callback.
   * Adyen v4.x passes (resolve, reject) as function parameters.
   */
  const handleClick = useCallback(
    (resolve, reject) => {
      if (typeof resolve !== 'function' || typeof reject !== 'function') {
        handleError(
          new Error('Invalid Google Pay onClick parameters'),
          t('google_pay_error.invalid_parameters'),
          'Adyen Google Pay onClick received invalid resolve/reject functions',
        );
        return;
      }

      // Adyen 4.9 wraps this onClick in a Promise with no rejection handler, so calling its
      // reject() surfaces an "unhandled promise rejection" (Unknown promise rejection reason).
      // The Google Pay sheet only opens once resolve() runs, so to abort before it opens we
      // simply never resolve. Expose a no-op reject to keep a uniform resolve/reject contract
      // for callers without rejecting Adyen's un-caught Promise.
      const abort = () => {};

      onPaymentButtonClick({
        resolve,
        reject: abort,
      });
    },
    [onPaymentButtonClick, handleError, t],
  );

  /**
   * Handles Google Pay payment submission.
   * In Adyen v4, onSubmit fires after the Google Pay sheet has already closed.
   * This is fire-and-forget from the component's perspective.
   * state.data contains paymentMethod (with googlePayToken) and browserInfo.
   *
   * @param {Object} state - Adyen component state with payment data.
   * @param {boolean} state.isValid - Indicates if the payment data is valid.
   * @param {Object} state.data - Payment data containing paymentMethod and browserInfo.
   * @param {Object} state.data.paymentMethod - Payment method object with type, googlePayToken, googlePayCardNetwork.
   * @param {Object} state.data.browserInfo - Browser info for 3DS (camelCase keys from SDK).
   * @param {Object} _component - Adyen Google Pay component instance (unused).
   */
  const handleSubmit = useCallback(
    // eslint-disable-next-line no-unused-vars
    async (state, _component) => {
      try {
        if (!state.isValid) {
          console.warn('Google Pay submit - state.isValid is false, skipping payment attempt');
          handleError(
            new Error('Google Pay state is not valid'),
            t('google_pay_error.invalid_state'),
            'Google Pay onSubmit called with invalid state',
          );
          return;
        }

        if (beforeWalletPaymentRef.current) {
          try {
            await beforeWalletPaymentRef.current();
          } catch (error) {
            dispatch(updatePurchase(false));
            return;
          }
        }

        dispatch(
          googlePayPaymentAttempt({
            googlePayPayload: state.data,
            purchaseToken,
          }),
        );
      } catch (err) {
        handleError(
          err,
          t('google_pay_error.processing_error'),
          'Error processing Google Pay submission',
        );
      }
    },
    [dispatch, purchaseToken, handleError, t],
  );

  return useAdyenGooglePaySdk({
    adyenConfig,
    currency,
    countryCode: env.country,
    merchantName: WhitelabelName,
    amount: purchaseTotal,
    onClick: handleClick,
    onSubmit: handleSubmit,
    onError: handleComponentError,
  });
}

export default useAdyenGooglePay;
